import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BarChart3, Users, Wrench, ChartBar, Sheet, Database, X, LineChart } from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';
import { DEFAULT_LANDING_ROUTE } from '../../routes/destinations';
import BottomNav from '../BottomNav/BottomNav';

// Tooltip rendered to the right of an icon — the only place a nav item's name
// is surfaced, since the rail is icon-only. Pure-CSS group-hover reveal — no
// JS, no flicker. The active item gets a subtle emerald accent so it stays
// visually distinct on hover.
function NavTooltip({ label, isActive }) {
  return (
    <span
      role="tooltip"
      className={`pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 z-[10000]
        whitespace-nowrap px-2.5 py-1 rounded-md text-[11px] font-medium tracking-tight
        bg-slate-800/95 backdrop-blur border shadow-xl shadow-black/40
        opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0
        transition-all duration-150 ${
          isActive
            ? 'border-emerald-500/30 text-emerald-300'
            : 'border-slate-700/60 text-slate-200'
        }`}
    >
      {/* Arrow / pointer */}
      <span
        aria-hidden="true"
        className={`absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 w-0 h-0
          border-y-[5px] border-y-transparent border-r-[5px] ${
            isActive ? 'border-r-emerald-500/30' : 'border-r-slate-700/60'
          }`}
      />
      <span
        aria-hidden="true"
        className="absolute left-0 top-1/2 -translate-x-[calc(100%-1px)] -translate-y-1/2 w-0 h-0
          border-y-[5px] border-y-transparent border-r-[5px] border-r-slate-800/95"
      />
      {label}
    </span>
  );
}

// Account control pinned to the sidebar footer. Opens an upward popover with
// the signed-in email + sign out. Mirrors the parent Snipe app's control; the
// only difference is sign-out routes through /logout so AuthContext clears
// the session and lands on the landing page.
function SideNavAccount() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const avatarLetter = (user?.email?.charAt(0) || 'A').toUpperCase();

  return (
    <div className="border-t border-slate-800/60 px-2 py-2 relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Account"
        className={`w-full flex items-center justify-center px-1 py-2 rounded-md transition-colors duration-150 hover:bg-slate-800/40 ${
          open ? 'bg-slate-800/40' : ''
        }`}
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
          {avatarLetter}
        </div>
      </button>

      {open && (
        <div className="absolute bottom-full mb-2 left-2 w-60 bg-[#0a1224] border border-slate-800 rounded-lg shadow-xl shadow-black/50 overflow-hidden z-[10001] animate-slide-up">
          <div className="px-3 py-2.5 border-b border-slate-800/80">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">Signed in</p>
            <p className="text-sm text-slate-200 truncate">{user?.email || 'operator@unknown'}</p>
          </div>
          <div className="py-1">
            <button
              onClick={() => navigate('/logout')}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-slate-400 hover:bg-slate-800/60 hover:text-red-400 transition-colors duration-150"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="text-sm font-medium">Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const DATASET_OPTION_GROUPS = [
  {
    title: 'Internal Counts',
    options: [
      { key: 'count1', label: 'BG 1', sub: 'Internal Count' },
      { key: 'count2', label: 'BG 2', sub: 'Internal Count' },
      { key: 'count3', label: 'BG 3', sub: 'Internal Count' },
      { key: 'count4', label: 'BG 4', sub: 'Internal Count' },
      { key: 'count5', label: 'BG 5', sub: 'Internal Count' },
    ],
  },
  {
    title: 'External Sources',
    options: [
      { key: 'betfair', label: 'Betfair', sub: 'External Count' },
      { key: 'espn', label: 'ESPN', sub: 'External Count' },
      { key: 'byb', label: 'BYB', sub: 'External Count' },
    ],
  },
];

// isMobileMenuOpen/setIsMobileMenuOpen are still passed by pages but unused:
// the mobile hamburger + drawer were replaced by the BottomNav bar.
const SideNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDatasetsOpen, setIsDatasetsOpen] = useState(false);
  const [draftDatasets, setDraftDatasets] = useState(null);
  const { hasAccess, isAuthenticated } = useAuth();
  // Account lacks the Brownlow entitlement: the coming-soon overlay is up, so
  // every control except the account/logout avatar is inert.
  const accessLocked = !hasAccess();
  const { enabledDatasets, applyEnabledDatasets } = useSettings();

  // Content areas offset themselves with var(--sidenav-width). The rail is a
  // fixed icon-only 72px — no expand/collapse.
  useEffect(() => {
    document.documentElement.style.setProperty('--sidenav-width', '72px');
  }, []);

  const menuItems = [
    { name: 'Player Leaderboard', path: '/dashboard', icon: BarChart3 },
    { name: 'Vote Progression', path: '/vote-progression', icon: LineChart },
    { name: 'Team Votes', path: '/team-votes', icon: ChartBar },
    { name: 'Player Comparison', path: '/player-comparison', icon: Users },
    { name: 'Game Votes', path: '/game-votes', icon: Sheet },
    { name: 'BYOBrownlow Tool', path: '/byo-brownlow', icon: Wrench },
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleMenuClick = (path) => {
    navigate(path);
  };

  const openDatasetsModal = () => {
    setDraftDatasets({ ...enabledDatasets });
    setIsDatasetsOpen(true);
  };

  return (
    <>
      {/* Sidebar — desktop only; mobile navigation is the BottomNav bar.
          z hops above the coming-soon overlay while locked so the account
          avatar stays reachable; normally it sits under page modals. */}
      <aside
        className={`fixed left-0 top-0 bottom-0 w-[72px] bg-[#060b18] border-r border-slate-800/60 hidden md:flex flex-col
          ${accessLocked ? 'z-[9999]' : 'z-40'}`}
        aria-label="Primary"
      >
        {/* Logo slot — fixed 72px tall to match the rail width. Signed-in
            users go back to the dashboard; a dead session falls through to
            the landing page. */}
        <div
          className="h-[72px] flex items-center justify-center border-b border-slate-800/60 cursor-pointer"
          onClick={() => navigate(isAuthenticated ? DEFAULT_LANDING_ROUTE : '/')}
          title="Brownlow Genius"
        >
          <img src="/favicon.svg" alt="Brownlow Genius" className="h-9 w-auto object-contain" />
        </div>

        {/* Nav */}
        {/* No overflow here — it would clip the hover tooltips that hang
            outside the rail. Six icons always fit the viewport. */}
        <nav className="flex-1 py-4 px-2">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              const active = !accessLocked && isActive(item.path);
              return (
                <li key={item.path} className="relative group">
                  {/* Active left rail */}
                  <span
                    aria-hidden="true"
                    className={`absolute -left-2 top-2 bottom-2 w-[2px] rounded-r transition-all duration-200 ${
                      active ? 'bg-emerald-400' : 'bg-transparent'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => handleMenuClick(item.path)}
                    disabled={accessLocked}
                    aria-label={item.name}
                    aria-current={active ? 'page' : undefined}
                    className={`w-full flex items-center justify-center px-1 py-2.5 rounded-md transition-colors duration-150 ${
                      accessLocked
                        ? 'text-slate-700'
                        : active
                          ? 'text-white bg-slate-800/40'
                          : 'text-slate-500 hover:text-white hover:bg-slate-800/30'
                    }`}
                  >
                    <IconComponent
                      className={`w-5 h-5 transition-colors ${active ? 'text-emerald-400' : 'text-current'}`}
                      strokeWidth={1.75}
                    />
                  </button>

                  <NavTooltip label={item.name} isActive={active} />
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer: datasets */}
        <div className="border-t border-slate-800/60 px-2 py-2 relative group">
          <button
            type="button"
            onClick={openDatasetsModal}
            disabled={accessLocked}
            aria-label="Manage which datasets are enabled"
            className={`w-full flex items-center justify-center px-1 py-2 rounded-md transition-colors duration-150 ${
              accessLocked
                ? 'text-slate-700'
                : 'text-slate-500 hover:text-white hover:bg-slate-800/30'
            }`}
          >
            <Database size={16} strokeWidth={1.75} />
          </button>
          <NavTooltip label="Datasets" isActive={false} />
        </div>

        {/* Account */}
        <SideNavAccount />
      </aside>

      {/* Mobile navigation — fixed bottom bar. Rendered here so it appears on
          every page (SideNav is mounted by each page) and can open the shared
          datasets modal below via its Account popup. */}
      <BottomNav onOpenDatasets={openDatasetsModal} />

      {/* Datasets modal */}
      {isDatasetsOpen && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-3 sm:p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsDatasetsOpen(false)} />
          <div className="relative w-full max-w-lg md:max-w-xl rounded-2xl border border-slate-700/60 bg-[#0f172a] shadow-2xl shadow-black/60 max-h-[88vh] md:max-h-[90vh] overflow-y-auto styled-scrollbar">
            {/* Modal header */}
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 md:px-6 py-3 border-b border-slate-700/50 bg-slate-800/95 backdrop-blur">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-1 h-4 bg-emerald-500" />
                <div>
                  <div className="text-sm font-semibold text-white tracking-tight">Enabled datasets</div>
                  <div className="text-[11px] text-slate-500">Changes apply instantly across tables and charts.</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Enabled-count chip: header on desktop, footer on mobile */}
                <span className="hidden md:inline-block px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[11px] font-mono font-semibold tabular-nums">{(() => {
                  const d = draftDatasets || {};
                  const cnt = ['count1','count2','count3','count4','count5','betfair','espn','byb'].reduce((a,k)=>a + (d[k] ? 1 : 0), 0);
                  return `${cnt} / 8`;
                })()}</span>
                <button
                  onClick={() => setDraftDatasets({ count1:true,count2:true,count3:true,count4:true,count5:true,betfair:true,espn:true,byb:true })}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-mono font-medium text-slate-300 border border-slate-700/70 hover:bg-slate-800/60 hover:text-slate-200 hover:border-slate-600 transition-all"
                >
                  Select all
                </button>
                <button
                  onClick={() => setIsDatasetsOpen(false)}
                  aria-label="Close datasets modal"
                  className="ml-1 p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800/60 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            {/* Modal body */}
            <div className="px-4 md:px-6 py-4 md:py-5">
              {/* Info section */}
              <div className="mb-4 p-3 rounded-xl bg-slate-900/40 border border-slate-700/50 text-slate-400 text-xs">
                Select which counts to include in the dataset.
              </div>
              {/* Internal and external groups sit side by side on desktop;
                  stacked on mobile where two columns would be too cramped. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-4 md:items-start">
                {DATASET_OPTION_GROUPS.map(({ title, options }) => (
                  <div key={title}>
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">{title}</h2>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-1 gap-2 md:gap-3">
                      {options.map(({ key, label, sub }) => {
                        const checked = !!(draftDatasets && draftDatasets[key]);
                        return (
                          <button
                            key={key}
                            type="button"
                            aria-pressed={checked ? 'true' : 'false'}
                            onClick={() => setDraftDatasets((prev) => ({ ...prev, [key]: !prev[key] }))}
                            className={`w-full text-left p-2 md:p-3 rounded-lg border transition-colors ${checked ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-slate-900/50 border-slate-800 hover:bg-slate-800/40'}`}
                          >
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                              <div className="flex-1 min-w-0 mb-1 md:mb-0">
                                <div className={`text-xs md:text-sm font-mono font-medium ${checked ? 'text-white' : 'text-slate-300'}`}>{label}</div>
                                <div className="text-[10px] md:text-[11px] text-slate-500 truncate">{sub}</div>
                              </div>
                              <div className={`self-end md:self-auto md:ml-3 h-4 w-4 md:h-5 md:w-5 rounded-full border flex items-center justify-center ${checked ? 'bg-emerald-500 border-emerald-400' : 'bg-transparent border-slate-600'}`}>
                                {checked && (
                                  <svg className="h-2.5 w-2.5 md:h-3.5 md:w-3.5 text-[#020817]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Modal footer */}
            <div className="sticky bottom-0 px-4 md:px-6 py-3 border-t border-slate-700/50 bg-[#0f172a] flex items-center justify-between md:justify-end">
              <span className="md:hidden inline-block px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[11px] font-mono font-semibold tabular-nums">{(() => {
                const d = draftDatasets || {};
                const cnt = ['count1','count2','count3','count4','count5','betfair','espn','byb'].reduce((a,k)=>a + (d[k] ? 1 : 0), 0);
                return `${cnt} / 8`;
              })()}</span>
              <button
                onClick={() => {
                  if (draftDatasets) applyEnabledDatasets(draftDatasets);
                  setIsDatasetsOpen(false);
                }}
                className="px-4 py-2 rounded-lg text-xs font-mono font-semibold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/15 hover:border-emerald-500/50 transition-all"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SideNav;
